import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress
} from "@mui/material";
import OwlCarousel from "react-owl-carousel";
import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

// Utility function to convert centimeters to feet and inches
const convertToFeetAndInches = (centimeters) => {
    if (!centimeters) return "N/A";
    const totalInches = centimeters / 2.54;
    const feet = Math.floor(totalInches / 12);
    const remainingInches = Math.round(totalInches % 12);
    return `${feet} feet ${remainingInches} inches`;
};

function ViewUserModalProfile({ show, handleClose, userId, token }) {
    const [userData, setUserData] = useState(null);
    const [sliderImages, setSliderImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL;

    // Fetch user data and gallery images when the modal opens
    useEffect(() => {
        if (userId && show) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    // Fetch user data
                    const userResponse = await axios.get(`${API_URL}/users/${userId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    setUserData(userResponse.data.data);

                    // Fetch gallery images
                    const galleryResponse = await axios.get(`${API_URL}/gallery/getbyid/${userId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    setSliderImages(galleryResponse.data.data || []);
                } catch (error) {
                    console.error("Error fetching data:", error);
                    toast.error("Failed to load profile data.");
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        }
    }, [userId, show, token]);

    return (
        <Dialog open={show} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                {userData ? `${userData.fname} ${userData.lname}'s Profile` : "Profile"}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <div>
                        {/* Photo Slider */}
                        {sliderImages.length > 0 ? (
                            <div className="find-woman-slider-wrap" style={{ marginBottom: "20px" }}>
                                <OwlCarousel
                                    className="owl-theme"
                                    items={1}
                                    nav
                                    dots
                                    loop
                                    margin={10}
                                    autoplay={true}
                                    autoplayTimeout={3000}
                                >
                                    {sliderImages.map((image, index) => (
                                        <div className="item" key={index}>
                                            <div className="user-img-box single-ladies-section">
                                                <img
                                                    src={`${API_URL}/${image.image}`}
                                                    alt={`Slide ${index}`}
                                                    style={{
                                                        maxWidth: "600px",
                                                        maxHeight: "400px",
                                                        width: "100%",
                                                        height: "auto",
                                                        objectFit: "contain",
                                                        display: "block",
                                                        margin: "0 auto",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </OwlCarousel>
                            </div>
                        ) : (
                            <Typography>No photos available</Typography>
                        )}

                        {/* Profile Details and Questions/Answers */}
                        {userData && (
                            <div className="row mt-3">
                                {/* Left Column: Profile Details */}
                                <div className="col-md-5">
                                    <p className="fs-6 mb-3">
                                        <span style={{ fontWeight: 600, color: "black" }}>Profile Number:</span>{" "}
                                        <span style={{ fontWeight: 500 }}>#FCW_{userData.id}</span>
                                    </p>
                                    <p className="fs-6 mb-3">
                                        <span style={{ fontWeight: 600, color: "black" }}>Location:</span>{" "}
                                        <span style={{ fontWeight: 500 }}>{userData.address || "N/A"}</span>
                                    </p>
                                    <p className="fs-6 mb-3">
                                        <span style={{ fontWeight: 600, color: "black" }}>Weight:</span>{" "}
                                        <span style={{ fontWeight: 500 }}>{userData.weight ? `${userData.weight}kg` : "N/A"}</span>
                                    </p>
                                    <p className="fs-6 mb-3">
                                        <span style={{ fontWeight: 600, color: "black" }}>Height:</span>{" "}
                                        <span style={{ fontWeight: 500 }}>
                                            {userData.height ? `${userData.height} cm (${convertToFeetAndInches(userData.height)})` : "N/A"}
                                        </span>
                                    </p>
                                    <p className="fs-6 mb-3">
                                        <span style={{ fontWeight: 600, color: "black" }}>Eye Color:</span>{" "}
                                        <span style={{ fontWeight: 500 }}>{userData.eyecolor || "N/A"}</span>
                                    </p>
                                    <p className="fs-6 mb-3">
                                        <span style={{ fontWeight: 600, color: "black" }}>Hair Color:</span>{" "}
                                        <span style={{ fontWeight: 500 }}>{userData.haircolor || "N/A"}</span>
                                    </p>
                                </div>

                                {/* Right Column: Questions and Answers */}
                                <div className="col-md-7">
                                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                        <div>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                Do you have children? If so, what are their ages?
                                            </Typography>
                                            <Typography fontWeight={600}>{userData.children_ages || "N/A"}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                What is your occupation or profession?
                                            </Typography>
                                            <Typography fontWeight={600}>{userData.occupation || "N/A"}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                What are your favorite hobbies or activities?
                                            </Typography>
                                            <Typography fontWeight={600}>{userData.hobbies || "N/A"}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                How would you describe your personality or characteristics?
                                            </Typography>
                                            <Typography fontWeight={600}>{userData.personality || "N/A"}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                What type of man do you wish to date or marry?
                                            </Typography>
                                            <Typography fontWeight={600}>{userData.person_to_marry || "N/A"}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                Where do you see yourself in the future with the right man?
                                            </Typography>
                                            <Typography fontWeight={600}>{userData.yourself_in_future || "N/A"}</Typography>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={loading}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ViewUserModalProfile;